/**
 * THE OPERATION REGISTRY — the only door from this app to the server, and the thing that makes
 * reachability a COMPILED FACT rather than a claim.
 *
 * ── WHAT THIS REPLACES, AND WHY ─────────────────────────────────────────────────────────────────
 *
 * `reachability.ts` used to map a route id to a SCREEN NAME, and its test proved that a `.tsx` file
 * with that basename existed. Two independent reviews found the same defect: the invariant the map
 * asserts — *"a person can perform this action"* — was never the property being checked. A screen
 * that renders a form and posts nothing satisfied it. `MfaChallengeScreen.tsx` exists, and its
 * submit handler sets a local boolean; `auth.mfa.verify` was certified reachable for months.
 *
 * The file-existence check could not have found that, and neither could a grep for URLs: the URLs
 * were built from template literals in fifteen different shapes. So the fix is not a better scanner.
 * It is to REMOVE the ability to write a URL in a screen at all.
 *
 * ── THE MECHANISM ───────────────────────────────────────────────────────────────────────────────
 *
 *   1. Every declared route is an entry here, keyed by `routeId`, carrying the method and the path
 *      template. `reachability.test.ts` asserts this table equals `registry/routes.baseline.json`
 *      — the approved artifact `write-registry.ts` regenerates and `route-baseline-diff` compares —
 *      in both directions, field for field. A route that changes its URL and does not change this
 *      table fails; a table entry naming a route the server does not serve fails.
 *
 *   2. A caller names an OPERATION, never a path: `callOperation('tenant.role.create', …)`. The id
 *      is typed as `OperationId`, so a misspelling is a compile error rather than a 404.
 *
 *   3. The URL IS BUILT HERE from the template and typed parameters. `ParamsOf<Id>` is extracted
 *      from the path template by the type system, so a call to an operation whose path contains
 *      `{tenant_uid}` does not compile without a `tenant_uid`, and a call that supplies a parameter
 *      the template has no slot for does not compile either.
 *
 *   4. The REQUEST SHAPE is `OperationBodies[Id]`. A write whose body is not declared here cannot be
 *      called: the type resolves to `UndeclaredBody`, which nothing satisfies. G-01's third defect
 *      was a body of `{email, password}` sent to a route reading `{login, password}` — a wrong field
 *      name at the right URL, answered by the endpoint's generic 401 and therefore indistinguishable
 *      from a wrong password. `reachability.test.ts` reconciles every field declared here against the
 *      field names the owning handler in `apps/api/src/routes/` actually reads.
 *
 *   5. Because every call site names a literal operation id, "which routes does this app reach" is
 *      answerable by reading the source — and `web-client-choke-point` (guardrail G-03) proves the
 *      two preconditions that make that reading COMPLETE: no module outside this file reaches the
 *      transport, and no `callOperation` is passed a computed id the scanner cannot resolve.
 *
 * ── WHAT THIS DOES NOT PROVE ────────────────────────────────────────────────────────────────────
 *
 * It proves the wire from a control in a screen to a declared route, with the method, the URL and
 * the field names the handler reads. It does NOT prove the gate allows the caller, that the service
 * behind the route does the right thing, or that the repository writes what the service intended —
 * those are `apps/api`'s own tests, and this file cannot see them. What it removes is the class of
 * defect where the browser half and the server half are each correct and do not meet.
 */

import { API_BASE, NetworkError, interpretResponse, requestInit } from './client';
import type {
  AuditEventSummary,
  CompanyGrantSummary,
  OffboardingStateView,
  PurgeEligibility,
  CompanySummary,
  MembershipSummary,
  SubscriptionSummary,
  SupportGrantSummary,
  TenantAdminSummary,
  RoleSummary,
  SessionSummary,
  UserSessionSummary,
  TenantSummary,
  TenantSupportGrantView,
} from './contract';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface OperationSpec {
  readonly method: HttpMethod;
  /** The DECLARED path, `/api`-prefixed, with `{param}` placeholders exactly as the baseline holds them. */
  readonly path: string;
}

/**
 * Every route in `registry/routes.baseline.json`, including the four MACHINE ones and every human
 * action no screen reaches yet.
 *
 * The whole set is here on purpose. A table containing only the operations somebody wired would make
 * "unreached" unrepresentable — the gap would be an absence, and an absence is what nobody counts.
 * Here an unreached route is an entry with no call site, which is a fact the scanner can state.
 */
export const OPERATIONS = {
  'auth.invitation.accept': { method: 'POST', path: '/api/auth/invitation' },
  'auth.mfa.enrol': { method: 'POST', path: '/api/auth/mfa/enrolment' },
  'auth.mfa.enrolment.confirm': { method: 'POST', path: '/api/auth/mfa/enrolment/confirm' },
  'auth.mfa.verify': { method: 'POST', path: '/api/auth/mfa' },
  'auth.password.reset_complete': { method: 'POST', path: '/api/auth/password/reset' },
  'auth.password.reset_request': { method: 'POST', path: '/api/auth/password/reset-request' },
  'auth.session.create': { method: 'POST', path: '/api/auth/session' },
  'auth.session.delete': { method: 'POST', path: '/api/auth/signout' },
  'auth.whoami.read': { method: 'GET', path: '/api/me' },
  'companies.company.create': { method: 'POST', path: '/api/companies' },
  'companies.company.lifecycle': { method: 'POST', path: '/api/companies/{company_uid}/lifecycle' },
  'companies.company.read': { method: 'GET', path: '/api/companies/{company_uid}' },
  'companies.company.update': { method: 'PUT', path: '/api/companies/{company_uid}' },
  'companies.conflict.read': { method: 'GET', path: '/api/companies/conflicts' },
  'companies.identifier.read': { method: 'GET', path: '/api/companies/{company_uid}/identifiers' },
  'companies.list.read': { method: 'GET', path: '/api/companies' },
  'companies.portfolio.assign': { method: 'PUT', path: '/api/companies/{company_uid}/portfolio' },
  'companies.source_binding.create': { method: 'PUT', path: '/api/companies/{company_uid}/source-bindings' },
  'connector.enrolment.redeem': { method: 'POST', path: '/api/connector/enrolment' },
  'connector.envelope.accept': { method: 'POST', path: '/api/connector/envelope' },
  'platform.discount.apply': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/discounts' },
  'platform.discount.apply_full': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/discounts/full' },
  'platform.discount.approve': { method: 'POST', path: '/api/platform/discounts/{discount_uid}/approval' },
  'platform.entitlement.explain': { method: 'POST', path: '/api/platform/entitlement/explain' },
  'platform.health.read': { method: 'GET', path: '/api/health' },
  'platform.limit.company.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/limits/company' },
  'platform.limit.seat.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/limits/seat' },
  'platform.subscription.create': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription' },
  'platform.subscription.plan.assign': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/plan' },
  'platform.subscription.sku.add': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/skus' },
  'platform.subscription.state.change': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/state' },
  'platform.support_grant.approve': { method: 'POST', path: '/api/platform/support-grants/{grant_uid}/approval' },
  'platform.support_grant.read': { method: 'GET', path: '/api/platform/support-grants' },
  'platform.support_grant.request': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/support-grants' },
  'platform.support_grant.revoke': { method: 'POST', path: '/api/platform/support-grants/{grant_uid}/revocation' },
  'platform.tenant.activate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/administrator' },
  'platform.tenant.close': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/closure' },
  'platform.tenant.create': { method: 'POST', path: '/api/platform/tenants' },
  'platform.tenant.offboarding.purge_eligibility': { method: 'GET', path: '/api/platform/tenants/{tenant_uid}/offboarding/purge-eligibility' },
  'platform.tenant.offboarding.read': { method: 'GET', path: '/api/platform/tenants/{tenant_uid}/offboarding' },
  'platform.tenant.offboarding.reinstate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/offboarding/reinstatement' },
  'platform.tenant.offboarding.terminate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/offboarding/termination' },
  'platform.tenant.reactivate': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/reactivation' },
  'platform.tenant.read': { method: 'GET', path: '/api/platform/tenants' },
  'platform.tenant.suspend': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/suspension' },
  'platform.trial.extend': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/trial/extension' },
  'platform.trial.set': { method: 'POST', path: '/api/platform/tenants/{tenant_uid}/subscription/trial' },
  'tenant.audit.read': { method: 'GET', path: '/api/tenant/audit' },
  'tenant.billing.read': { method: 'GET', path: '/api/tenant/subscription' },
  'tenant.branding.update': { method: 'PUT', path: '/api/tenant/branding' },
  'tenant.connector.enrolment.mint': { method: 'POST', path: '/api/tenant/connectors/enrolment-tokens' },
  'tenant.connector.list': { method: 'GET', path: '/api/tenant/connectors' },
  'tenant.connector.revoke': { method: 'POST', path: '/api/tenant/connectors/{connector_id}/revocation' },
  'tenant.grant.create': { method: 'POST', path: '/api/tenant/grants' },
  'tenant.grant.read': { method: 'GET', path: '/api/tenant/grants' },
  'tenant.grant.revoke': { method: 'DELETE', path: '/api/tenant/grants' },
  'tenant.member.read': { method: 'GET', path: '/api/tenant/members' },
  'tenant.role.create': { method: 'POST', path: '/api/tenant/roles' },
  'tenant.role.read': { method: 'GET', path: '/api/tenant/roles' },
  'tenant.role.update': { method: 'PUT', path: '/api/tenant/roles/{role_code}' },
  // CM-012 / CM-058. The office's own view of support access into its data, and the two acts it
  // may take on it. The capabilities were seeded with zero routes while the security screen told
  // the office in writing that it could revoke at any moment.
  'security.support_grant.consent': { method: 'POST', path: '/api/tenant/support-grants/{grant_uid}/consent' },
  'security.support_grant.read': { method: 'GET', path: '/api/tenant/support-grants' },
  'security.support_grant.revoke': { method: 'POST', path: '/api/tenant/support-grants/{grant_uid}/revocation' },
  'tenant.source.configure': { method: 'PUT', path: '/api/tenant/sources/{connection_uid}/configuration' },
  'tenant.source.create': { method: 'POST', path: '/api/tenant/sources' },
  'tenant.source.external_company.list': { method: 'GET', path: '/api/tenant/sources/{connection_uid}/external-companies' },
  'tenant.source.external_company.resolve': { method: 'GET', path: '/api/tenant/sources/{connection_uid}/binding' },
  'tenant.source.health': { method: 'GET', path: '/api/tenant/sources/{connection_uid}/health' },
  'tenant.source.list': { method: 'GET', path: '/api/tenant/sources' },
  'tenant.source.probe': { method: 'POST', path: '/api/tenant/sources/{connection_uid}/connectivity-check' },
  'tenant.source.revoke': { method: 'POST', path: '/api/tenant/sources/{connection_uid}/revocation' },
  'tenant.user.disable': { method: 'POST', path: '/api/tenant/users/disable' },
  'tenant.user.invitation.resend': { method: 'POST', path: '/api/tenant/users/invitations/{invitation_uid}/resend' },
  'tenant.user.invite': { method: 'POST', path: '/api/tenant/users/invitations' },
  'tenant.user.reactivate': { method: 'POST', path: '/api/tenant/users/reactivate' },
  'tenant.user.role.assign': { method: 'PUT', path: '/api/tenant/users/roles' },
  'tenant.user.session.read': { method: 'GET', path: '/api/tenant/users/{membership_uid}/sessions' },
  'tenant.user.session.revoke': { method: 'POST', path: '/api/tenant/users/{membership_uid}/sessions/revoke' },
  'user.session.read': { method: 'GET', path: '/api/me/sessions' },
  'user.session.revoke_all': { method: 'POST', path: '/api/me/sessions/revoke' },
} as const satisfies Readonly<Record<string, OperationSpec>>;

export type OperationId = keyof typeof OPERATIONS;

export const OPERATION_IDS: readonly OperationId[] = Object.keys(OPERATIONS) as OperationId[];

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE REQUEST SHAPES
//
// One entry per operation this app is permitted to call. An operation absent from here CANNOT be
// called — see `UndeclaredBody` — which is why wiring a new action is a deliberate act that states
// what it sends, and why `reachability.test.ts` can reconcile every field name below against the
// handler that reads it.
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * WHO THE SERVER SAYS THE CALLER IS. **CM-030.**
 *
 * Field for field what `services/session-identity.ts` returns. This is the ONLY thing the browser
 * derives its surface from — CM-014 removed the two mechanisms that competed with it, an address
 * that minted a session and a button that set one.
 */
export interface WhoamiResponse {
  readonly userUid: string;
  readonly displayName: string;
  /** `'platform' | 'tenant'`. Fixed for the life of the account, and NOT the surface. */
  readonly accountKind: string;
  /** The SESSION's context. A platform account under a support grant is in `TENANT`. */
  readonly context: string;
  readonly tenantUid: string | null;
  readonly tenants: readonly TenantSummary[];
  readonly capabilities: readonly string[];
  readonly supportGrantUid: string | null;
}

/**
 * The wire answer, as the shell's `SessionSummary`.
 *
 * A translation and not a widening: `context` is narrowed to the two surfaces this product has, and
 * anything else becomes `null` — an unrecognised context is not a surface, and defaulting it to
 * TENANT would grant one. That is the CM-014 defect in miniature, so it is refused here too.
 */
export function sessionFromWhoami(me: WhoamiResponse): SessionSummary | null {
  const context = me.context === 'PLATFORM' || me.context === 'TENANT' ? me.context : null;
  if (context === null) return null;
  return {
    userUid: me.userUid,
    displayName: me.displayName,
    sessionContext: context,
    tenants: me.tenants,
    activeTenantUid: me.tenantUid,
    capabilities: me.capabilities,
    supportGrantUid: me.supportGrantUid,
  };
}

/** The four-arm answer to a sign-in. `REFUSED` never arrives — the route answers it as a 401. */
export type SignInResponse =
  | { readonly outcome: 'SESSION' }
  | { readonly outcome: 'MFA_REQUIRED'; readonly challengeRef: string; readonly expiresAt: string }
  | { readonly outcome: 'MFA_ENROLMENT_REQUIRED'; readonly challengeRef: string; readonly expiresAt: string };

export interface OperationBodies {
  'auth.session.create': { readonly login: string; readonly password: string };
  /** Takes nothing. The subject is the session holder, and there is no field that could name another. */
  'auth.mfa.enrol': Record<string, never>;
  'auth.mfa.enrolment.confirm': { readonly challengeRef: string; readonly code: string };
  /**
   * `factor` is REQUIRED and is never inferred from the shape of the code.
   *
   * The handler says why: trying TOTP and then a recovery code on one submission spends two
   * rate-limit budgets and makes the RESPONSE TIME say which one ran. The caller states which
   * factor the person is presenting, and one submission tries exactly one thing.
   */
  /* The invitee CHOOSES the credential. Nothing is generated, echoed or returned — this is what
     replaces Legacy create-user, which hard-coded 'changeme' and put it in the JSON response. */
  'auth.invitation.accept': { readonly token: string; readonly password: string };
  /* A reset re-establishes the FIRST factor only, and issues no session on any branch. */
  'auth.password.reset_complete': { readonly token: string; readonly password: string };
  /* The address only. The answer is identical whether or not it is known — anything else would be
     an account-enumeration oracle, and the token reaches the MAILBOX, never a response body. */
  'auth.password.reset_request': { readonly login: string };
  /* `logoAssetRef` is OPTIONAL and may be cleared. The handler reads a non-string as `null`. */
  'tenant.branding.update': {
    readonly displayNameHe: string;
    readonly logoAssetRef?: string | null;
  };
  'auth.mfa.verify': {
    readonly challengeRef: string;
    readonly code: string;
    readonly factor: 'totp' | 'recovery_code';
  };
  'user.session.revoke_all': Record<string, never>;
  /** Ends THIS session. Takes nothing — the session is the cookie, and there is no field for one. */
  'auth.session.delete': Record<string, never>;

  // CM-012 / CM-058. Consent names no grant in its body — the grant is the path, and the office is
  // the bound context. There is no field here through which one office could consent for another.
  'security.support_grant.consent': Record<string, never>;
  'security.support_grant.revoke': { readonly reason: string };

  'platform.tenant.create': { readonly slug: string; readonly legalName: string; readonly kind: string };
  'platform.subscription.create': {
    readonly planCode: string;
    readonly periodKind: string;
    readonly trialEndsAt: string | null;
  };
  'platform.subscription.sku.add': { readonly skuCode: string; readonly quantity: number };
  'platform.tenant.activate': { readonly email: string; readonly displayName: string };

  'platform.tenant.suspend': { readonly reason: string };
  'platform.tenant.reactivate': { readonly reason: string };
  'platform.tenant.close': { readonly reason: string };

  'platform.tenant.offboarding.terminate': {
    readonly reason: string;
    readonly exportDisposition: ExportDispositionBody;
  };
  'platform.tenant.offboarding.reinstate': { readonly reason: string };

  'platform.subscription.plan.assign': { readonly planCode: string; readonly reason: string };
  'platform.subscription.state.change': { readonly toState: string; readonly reason: string };
  'platform.trial.set': { readonly trialEndsAt: string | null; readonly reason: string };
  'platform.trial.extend': { readonly trialEndsAt: string; readonly reason: string };
  'platform.limit.company.set': { readonly limit: number | null; readonly reason: string };
  'platform.limit.seat.set': { readonly limit: number | null; readonly reason: string };

  'platform.discount.apply': DiscountBody;
  'platform.discount.apply_full': DiscountBody;
  'platform.discount.approve': Record<string, never>;

  'platform.support_grant.request': {
    readonly reason: string;
    readonly ticketRef: string | null;
    readonly mode: string;
    readonly requiresConsent: boolean;
    readonly capabilitySet: readonly string[];
    readonly isRelatedParty: boolean;
  };
  'platform.support_grant.approve': Record<string, never>;
  'platform.support_grant.revoke': { readonly reason: string };

  'companies.company.create': {
    readonly legalName: string;
    readonly displayName: string;
    readonly entityKind: string;
    readonly taxId: string | null;
  };
  'companies.company.update': {
    readonly legalName: string;
    readonly displayName: string;
    readonly entityKind: string;
    readonly taxId: string | null;
  };
  'companies.company.lifecycle': { readonly to: string; readonly mergedIntoCompanyUid?: string };
  /**
   * PRESENTATION, NEVER PERMISSION. The handler reads `membershipUid` and `kind` — a partner and the
   * sort of relationship, not a free-text code. The screen sent `{portfolioCode}` until this table
   * made the mismatch a compile error.
   */
  'companies.portfolio.assign': { readonly membershipUid: string; readonly kind: string };
  'companies.source_binding.create': {
    readonly connectionUid: string;
    readonly externalCompanyId: string;
    readonly confidence: string;
    readonly note: string;
    readonly taxIdCheckDigitValid: boolean | null;
  };

  'tenant.source.configure': SourceConfigureBody;
  'tenant.source.probe': Record<string, never>;

  'tenant.grant.create': {
    readonly membershipUid: string;
    readonly companyUids: readonly string[];
    readonly accessLevel: string;
  };
  'tenant.grant.revoke': { readonly membershipUid: string; readonly companyUid: string };

  'tenant.role.create': {
    readonly roleCode: string;
    readonly displayNameHe: string;
    readonly capabilities: readonly string[];
  };
  'tenant.role.update': { readonly displayNameHe: string; readonly capabilities: readonly string[] };
  'tenant.user.role.assign': { readonly membershipUid: string; readonly roleCodes: readonly string[] };

  'tenant.user.invite': { readonly displayName: string; readonly email: string };
  'tenant.user.disable': { readonly membershipUid: string; readonly reason: string };
  'tenant.user.reactivate': { readonly membershipUid: string; readonly reason: string };
  'tenant.user.invitation.resend': Record<string, never>;
  'tenant.user.session.revoke': { readonly reason: string };
}

/**
 * ALL SIX FIELDS ARE REQUIRED BY THE HANDLER, AND EVERY ONE OF THEM IS OPTIONAL HERE.
 *
 * That is not a contradiction; it is the refusal contract. `tenant.source.configure` answers
 * `invalid_request` unless all six are present, and `SourceConnectionScreen` renders the server's
 * list of everything that was missing (07 N-5: never the first only). If this type required them,
 * `configurationBody` would have to invent an empty string for an unfilled control — which is the
 * defect 07 N-5 records, a missing `data_source_type` becoming a working default on 5 of 313
 * companies because nothing was willing to hold the absence.
 *
 * There is no `password`, no `token` and no `connectionString`, matching the handler exactly.
 */
export interface SourceConfigureBody {
  readonly displayNameHe?: string;
  readonly sourceSystemCode?: string;
  readonly endpointRef?: string;
  readonly secretRef?: string;
  readonly textEncoding?: string;
  readonly sourceDateFormat?: string;
}

/** A discount, in the two shapes it is applied in. `apply_full` is a separate ACTION, not a bigger number. */
export interface DiscountBody {
  readonly discountKind: string;
  /** A percentage is a number. An AMOUNT is a string and stays one — 0003. */
  readonly discountPct: number | null;
  readonly discountAmount: string | null;
  readonly currencyCode: string | null;
  readonly scope: string;
  readonly scopeSkuCode: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly reason: string;
  /** DECLARED, never defaulted. OA-0002. */
  readonly isRelatedParty: boolean;
}

/** No arm for "not answered". A termination request with no disposition is not built. */
export type ExportDispositionBody =
  | { readonly kind: 'REQUESTED' }
  | { readonly kind: 'ALREADY_DELIVERED'; readonly exportRequestUid: string }
  | { readonly kind: 'WAIVED'; readonly detail: string };

/** The answers this app reads. An operation absent from here answers `void`. */
export interface OperationResults {
  'auth.session.create': SignInResponse;
  /**
    * CM-024. The declared result was `{ challengeRef }` alone, so the screen had nothing to show and
    * rendered a FIXTURE SECRET instead — a fixed, published Base32 string, offered to every user as
    * the key to put in their authenticator, while the confirmation went to the server's real
    * challenge. Enrolment could never verify, and anyone who scanned it enrolled a shared secret.
    *
    * The server has always returned `provisioning`. Nothing needed to be built on that side; what
    * was missing was this declaration and a screen that read it.
    */
  'auth.mfa.enrol': {
    readonly challengeRef: string;
    readonly expiresAt: string;
    /** The `otpauth://` URI. **IT CONTAINS THE SHARED SECRET.** Displayed once, stored nowhere. */
    readonly provisioning: string;
  };
  /** The recovery codes exist only AFTER a code proves the secret reached the device. */
  'auth.mfa.enrolment.confirm': { readonly outcome: string; readonly recoveryCodes: string };
  /* Success is a SESSION — the cookie is set by the response, and this body only names the outcome. */
  'auth.mfa.verify': { readonly outcome: string };
  'auth.invitation.accept': { readonly ok: boolean };
  'auth.password.reset_complete': { readonly accepted: boolean };
  /* Always the same body, whether or not the address is known. Deliberately carries nothing. */
  'auth.password.reset_request': { readonly requested: boolean };
  'tenant.branding.update': { readonly ok: boolean };
  'auth.whoami.read': WhoamiResponse;
  'auth.session.delete': { readonly ok?: boolean };
  /* Phase 2 §13 — the reads screens used to render from fixtures. Shapes mirror `contract.ts`. */
  'tenant.member.read': { readonly members: readonly MembershipSummary[] };
  'tenant.role.read': { readonly roles: readonly RoleSummary[] };
  'tenant.grant.read': { readonly grants: readonly CompanyGrantSummary[] };
  'companies.list.read': { readonly companies: readonly CompanySummary[] };
  /* The handler answers with the summary itself, not a wrapper — and a 404 when there is none. */
  'tenant.billing.read': SubscriptionSummary;
  'platform.tenant.read': { readonly tenants: readonly TenantAdminSummary[] };
  'platform.support_grant.read': { readonly grants: readonly SupportGrantSummary[] };
  'tenant.audit.read': { readonly events: readonly AuditEventSummary[] };
  'platform.tenant.offboarding.read': OffboardingStateView;
  /**
   * The SAME `PurgeEligibility` the view above carries, from its own route.
   *
   * Two routes answering with one shape is not duplication here: the state read is a snapshot of the
   * card, and the eligibility read is the question *"may the governed deletion workflow begin right
   * now"* — asked at the moment somebody is about to act, against a retention clock that runs. A
   * card rendered ten minutes ago is not an answer to that.
   */
  'platform.tenant.offboarding.purge_eligibility': PurgeEligibility;
  'user.session.read': { readonly sessions: readonly UserSessionSummary[] };
  'tenant.user.session.read': { readonly sessions: readonly UserSessionSummary[] };
  'user.session.revoke_all': { readonly revoked: boolean; readonly count: number };
  'security.support_grant.read': { readonly grants: readonly TenantSupportGrantView[] };
  'security.support_grant.consent': { readonly consented: boolean; readonly grantUid: string };
  'security.support_grant.revoke': { readonly revoked: boolean; readonly grantUid: string };
  'platform.tenant.create': { readonly tenantUid: string };
  'platform.tenant.activate': { readonly invitationUid: string };
  'platform.discount.apply': { readonly outcome?: string };
  'platform.discount.apply_full': { readonly outcome?: string };
  'platform.subscription.plan.assign': { readonly overCapacityBy?: number };
  'platform.subscription.sku.add': { readonly overCapacityBy?: number };
  'platform.subscription.state.change': { readonly overCapacityBy?: number };
  'platform.trial.set': { readonly overCapacityBy?: number };
  'platform.trial.extend': { readonly overCapacityBy?: number };
  'platform.limit.company.set': { readonly overCapacityBy?: number };
  'platform.limit.seat.set': { readonly overCapacityBy?: number };
  'platform.tenant.offboarding.reinstate': { readonly mustReestablish?: unknown };
  'platform.tenant.offboarding.terminate': { readonly revoked?: unknown };
  'companies.company.create': { readonly companyUid: string };
  'tenant.grant.create': { readonly granted: number };
  'tenant.role.create': { readonly ok?: boolean };
  'tenant.user.role.assign': { readonly ok: boolean; readonly roleCount: number };
  'tenant.user.invite': { readonly invitationUid: string; readonly delivery?: unknown };
  'tenant.user.invitation.resend': { readonly delivery?: unknown };
  'tenant.user.session.revoke': { readonly revoked?: number };
  'tenant.source.probe': {
    readonly ok: boolean;
    readonly failure?: { readonly kind?: string };
    readonly checkedAt?: string;
  };
}

/**
 * The type an undeclared write body resolves to. Nothing inhabits it, so the call does not compile
 * and the error names the fix. This is the mechanism that makes "wire a new action" a decision:
 * a screen cannot start posting to a route without stating what it posts.
 */
export interface UndeclaredBody {
  readonly __declareThisOperationInOperationBodies: never;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE TYPE-LEVEL PATH CONTRACT
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/** `'/api/x/{a}/y/{b}'` → `'a' | 'b'`. The URL's own parameters, read by the compiler. */
type PathParam<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | PathParam<Rest>
  : never;

export type ParamsOf<Id extends OperationId> = PathParam<(typeof OPERATIONS)[Id]['path']>;

type MethodOf<Id extends OperationId> = (typeof OPERATIONS)[Id]['method'];

type BodyOf<Id extends OperationId> = Id extends keyof OperationBodies
  ? OperationBodies[Id]
  : UndeclaredBody;

export type ResultOf<Id extends OperationId> = Id extends keyof OperationResults
  ? OperationResults[Id]
  : void;

type ParamsField<Id extends OperationId> = [ParamsOf<Id>] extends [never]
  ? { readonly params?: never }
  : { readonly params: Readonly<Record<ParamsOf<Id>, string>> };

type BodyField<Id extends OperationId> = MethodOf<Id> extends 'GET'
  ? { readonly body?: never }
  : { readonly body: BodyOf<Id> };

export type CallOptions<Id extends OperationId> = ParamsField<Id> &
  BodyField<Id> & { readonly signal?: AbortSignal };

/**
 * A compile-time assertion that `OperationBodies` and `OperationResults` name only declared
 * operations. Without it a typo in a key would silently create a shape no call site can reach — the
 * "written, correct, reaches nothing" defect, at the type level.
 */
type UnknownKeys = Exclude<keyof OperationBodies | keyof OperationResults, OperationId>;
const _everyDeclaredShapeNamesARealOperation: [UnknownKeys] extends [never] ? true : UnknownKeys =
  true;
void _everyDeclaredShapeNamesARealOperation;

// ══════════════════════════════════════════════════════════════════════════════════════════════════
// THE CALL
// ══════════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Substitutes the declared parameters into the declared template.
 *
 * Every value is percent-encoded, and the result is asserted to carry no leftover placeholder. A
 * remaining `{` would mean the type-level extraction and the runtime substitution disagree, which is
 * a programming error rather than a user-visible failure, so it throws where a developer sees it.
 *
 * Exported for `reachability.test.ts`, which builds every operation's URL from sample parameters and
 * compares it against the baseline path — so the substitution itself is covered rather than assumed.
 */
export function buildOperationPath(
  spec: OperationSpec,
  params: Readonly<Record<string, string>> | undefined,
): string {
  const filled = spec.path.replace(/\{([a-z_0-9]+)\}/gu, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === '') {
      throw new Error(
        `operation path ${spec.path} needs a value for {${name}} — an address built from an absent ` +
          'identifier would be a request against whatever the server resolves the empty segment to',
      );
    }
    return encodeURIComponent(value);
  });
  if (filled.includes('{') || filled.includes('}')) {
    throw new Error(`operation path ${spec.path} still holds a placeholder after substitution`);
  }
  return filled;
}

/**
 * THE ONLY WAY THIS APP TALKS TO THE SERVER.
 *
 * `client.ts` still owns the transport, the error taxonomy and the retry policy — a write is never
 * retried, and that rule stays where the reasoning for it is written. This adds the layer above it:
 * WHICH endpoint, with WHICH parameters, in WHICH shape, all decided by the table above rather than
 * by a template literal in a screen.
 *
 * `web-client-choke-point` (G-03) fails the build if any module under `apps/web/src` other than
 * `api/client.ts` and this file imports the transport directly, and if any call below is passed an
 * operation id the static scan cannot resolve to a literal. Those two rules are what let
 * `reachability.test.ts` say "these routes are reached and these are not" and be believed.
 */
type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/**
 * Injectable for tests, and INERT OUTSIDE THEM.
 *
 * `globalThis.fetch` below is the only reference to the network in the whole app — the property
 * `web-client-choke-point` now enforces. This seam is the one way that property could be undone
 * from inside: a screen importing it could install a `fetch` of its own and issue anything.
 *
 * So it refuses outside a test build. That is a structural answer rather than another import
 * prohibition, because CM-013 is the finding that import prohibitions get spelled around — and a
 * seam that is dead in the shipped bundle cannot be spelled around at all.
 */
let fetchImpl: FetchLike | null = null;
export function __setFetchForTests(f: FetchLike | null): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error(
      'the transport seam is a TEST seam. A module that could replace fetch could issue any ' +
        'request it liked, which is exactly what the operation registry exists to prevent.',
    );
  }
  fetchImpl = f;
}

/**
 * HOW MANY TIMES A REQUEST MAY BE ATTEMPTED — **DERIVED FROM THE DECLARATION, NEVER PASSED IN.**
 *
 * This used to be a `retries` option on `request()`, with a runtime throw if a caller passed it
 * alongside a write. That check was correct and it was also an admission: the shape allowed the
 * dangerous call to be WRITTEN, so it needed guarding. Here there is no argument through which a
 * caller could ask for a write to be retried, so "a write is never retried" is a property of the
 * type rather than a rule somebody enforces.
 *
 * The reason has not changed: a client that resends a POST after the connection died cannot know
 * whether the server committed the first one. A GET may be resent because asking twice is asking.
 */
function attemptsFor(method: HttpMethod): number {
  return method === 'GET' ? 2 : 1;
}

export function callOperation<Id extends OperationId>(
  id: Id,
  options: CallOptions<Id>,
): Promise<ResultOf<Id>> {
  const spec: OperationSpec = OPERATIONS[id];
  return issue<ResultOf<Id>>(spec, buildOperationPath(spec, options.params), options);
}

/**
 * THE ONE PLACE THIS APP TOUCHES THE NETWORK. **CM-013.**
 *
 * It is `function`-private to this module: not exported, so there is nothing for another module to
 * import under any spelling. That is the correction. The rule used to be *"only these two files may
 * import `request`/`get`/`post`"*, enforced by scanning import statements — and three mutations beat
 * it with CI green (a namespace import, a `./client.js` specifier, a re-export through a third
 * module), while a screen calling `fetch` directly was never covered by the rule at all.
 *
 * `path` is built by `buildOperationPath` from the approved baseline template, so no free-form
 * address reaches here even from inside this file.
 */
async function issue<T>(
  spec: OperationSpec,
  fullPath: string,
  options: { readonly body?: unknown; readonly signal?: AbortSignal },
): Promise<T> {
  // The table holds the DECLARED path, which is `/api`-prefixed; the fetch below re-applies it, so
  // the prefix is removed once here rather than being absent from the baseline.
  const path = fullPath.slice(API_BASE.length);
  const init = requestInit({
    method: spec.method,
    ...(options.body === undefined ? {} : { body: options.body }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const attempts = attemptsFor(spec.method);
  let lastNetworkFailure: NetworkError | null = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    let res: Response;
    try {
      res = await (fetchImpl ?? globalThis.fetch)(`${API_BASE}${path}`, init);
    } catch {
      lastNetworkFailure = new NetworkError();
      // Only reachable for a GET; every other method left the loop at attempts === 1.
      continue;
    }
    return interpretResponse<T>(res);
  }

  throw lastNetworkFailure ?? new NetworkError();
}
